// Allowlist sanitiser for email bodies drafted by the AI chat agent. The input
// is model output, so it is treated as hostile: only the tags below survive,
// `a[href]` is the only attribute, and everything else is reduced to its text.
// A hand-rolled linear tokenizer rather than a regex sanitiser: the output is
// rebuilt from tokens, so whatever the tokenizer misreads can only ever come out
// as escaped text, never as live markup.

const ALLOWED_TAGS = new Set([
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'ul',
  'ol',
  'li',
  'blockquote',
  'a',
]);

const BLOCK_TAGS = new Set(['p', 'ul', 'ol', 'li', 'blockquote']);
const TOP_LEVEL_BLOCK_TAGS = new Set(['p', 'ul', 'ol', 'blockquote']);
// Children of these are blocks, so whitespace directly inside them is not text.
const BLOCK_CONTAINER_TAGS = new Set(['ul', 'ol', 'blockquote']);

// The composer renders paragraphs with no margin: a blank line between two
// paragraphs is an empty paragraph, as pressing Enter twice produces.
const BLANK_LINE = '<p><br></p>';

// Content is dropped with the tag. Raw-text elements end at the first matching
// end tag, whatever is in between; the others can nest.
const RAW_TEXT_DROPPED_TAGS = new Set([
  'script',
  'style',
  'iframe',
  'noscript',
  'title',
]);
const NESTED_DROPPED_TAGS = new Set([
  'object',
  'template',
  'svg',
  'math',
  'head',
]);

const ALLOWED_HREF_SCHEMES = new Set(['http', 'https', 'mailto']);

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: '\u00a0',
  colon: ':',
  tab: '\t',
  newline: '\n',
  sol: '/',
  period: '.',
  lpar: '(',
  rpar: ')',
  num: '#',
};

type ParsedTag = {
  name: string;
  closing: boolean;
  selfClosing: boolean;
  attrs: Map<string, string>;
  end: number;
};

const isSpace = (ch: string | undefined): boolean =>
  ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' || ch === '\f';

const isAsciiAlpha = (ch: string | undefined): boolean =>
  ch != null && /^[A-Za-z]$/.test(ch);

const escapeHtmlText = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Text between tags keeps well-formed character references as written
// (`&lt;`, `&#233;`) and escapes everything else.
const escapeSourceText = (text: string): string =>
  text
    .replace(
      /&(?!(?:#[0-9]{1,7}|#[xX][0-9a-fA-F]{1,6}|[A-Za-z][A-Za-z0-9]{1,31});)/g,
      '&amp;',
    )
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const escapeAttr = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const fromCodePoint = (codePoint: number): string =>
  codePoint === 0 ||
  codePoint > 0x10ffff ||
  (codePoint >= 0xd800 && codePoint <= 0xdfff)
    ? '\ufffd'
    : String.fromCodePoint(codePoint);

const decodeCharacterReferences = (value: string): string =>
  value.replace(
    /&(?:#([0-9]{1,7});?|#[xX]([0-9a-fA-F]{1,6});?|([A-Za-z][A-Za-z0-9]*);)/g,
    (match, decimal?: string, hex?: string, named?: string) => {
      if (decimal != null) {
        return fromCodePoint(Number.parseInt(decimal, 10));
      }
      if (hex != null) {
        return fromCodePoint(Number.parseInt(hex, 16));
      }
      return NAMED_ENTITIES[named?.toLowerCase() ?? ''] ?? match;
    },
  );

const trimC0ControlOrSpace = (value: string): string => {
  let start = 0;
  let end = value.length;
  while (start < end && value.charCodeAt(start) <= 0x20) {
    start += 1;
  }
  while (end > start && value.charCodeAt(end - 1) <= 0x20) {
    end -= 1;
  }
  return value.slice(start, end);
};

// Returns the URL to emit, or null to drop the attribute. The value checked is
// exactly the value emitted (re-escaped), so an entity this decoder does not
// know stays literal text and cannot smuggle a scheme past the check.
export const sanitizeAgentHref = (raw: string): string | null => {
  // The URL parser drops tab/LF/CR anywhere and C0 controls/space at the ends.
  const decoded = trimC0ControlOrSpace(
    decodeCharacterReferences(raw).replace(/[\t\n\r]/g, ''),
  );
  const scheme = /^([A-Za-z][A-Za-z0-9+.-]*):/.exec(decoded)?.[1];
  if (scheme == null || !ALLOWED_HREF_SCHEMES.has(scheme.toLowerCase())) {
    return null;
  }
  return decoded;
};

// Parses the tag opening at `start` (which holds `<`). Returns null when the
// input ends inside the tag: the HTML tokenizer drops such a tag, so do we.
const readTag = (html: string, start: number): ParsedTag | null => {
  let i = start + 1;
  const closing = html[i] === '/';
  if (closing) {
    i += 1;
  }
  const nameStart = i;
  while (
    i < html.length &&
    !isSpace(html[i]) &&
    html[i] !== '/' &&
    html[i] !== '>'
  ) {
    i += 1;
  }
  const name = html.slice(nameStart, i).toLowerCase();
  const attrs = new Map<string, string>();
  let selfClosing = false;

  for (;;) {
    while (isSpace(html[i]) || html[i] === '/') {
      selfClosing = html[i] === '/';
      i += 1;
    }
    if (i >= html.length) {
      return null;
    }
    if (html[i] === '>') {
      return { name, closing, selfClosing, attrs, end: i + 1 };
    }
    selfClosing = false;

    const attrStart = i;
    i += 1;
    while (
      i < html.length &&
      !isSpace(html[i]) &&
      html[i] !== '/' &&
      html[i] !== '>' &&
      html[i] !== '='
    ) {
      i += 1;
    }
    const attrName = html.slice(attrStart, i).toLowerCase();
    while (isSpace(html[i])) {
      i += 1;
    }

    let attrValue = '';
    if (html[i] === '=') {
      i += 1;
      while (isSpace(html[i])) {
        i += 1;
      }
      const quote = html[i];
      if (quote === '"' || quote === "'") {
        const valueEnd = html.indexOf(quote, i + 1);
        if (valueEnd === -1) {
          return null;
        }
        attrValue = html.slice(i + 1, valueEnd);
        i = valueEnd + 1;
      } else {
        const valueStart = i;
        while (i < html.length && !isSpace(html[i]) && html[i] !== '>') {
          i += 1;
        }
        attrValue = html.slice(valueStart, i);
      }
    }
    if (!attrs.has(attrName)) {
      attrs.set(attrName, attrValue);
    }
  }
};

// Index just past the end tag closing a raw-text element opened before `from`,
// or the input length when it is never closed.
const skipRawText = (html: string, from: number, name: string): number => {
  const endTagRe = new RegExp(`</${name}(?=[\\s/>]|$)`, 'gi');
  endTagRe.lastIndex = from;
  const match = endTagRe.exec(html);
  if (match == null) {
    return html.length;
  }
  return readTag(html, match.index)?.end ?? html.length;
};

const plainTextToHtml = (text: string): string =>
  text
    .replace(/\r\n?/g, '\n')
    .split(/\n{2,}/)
    .map(paragraph => paragraph.replace(/^\n+|\n+$/g, ''))
    .filter(paragraph => paragraph.trim() !== '')
    .map(
      paragraph =>
        `<p>${paragraph.split('\n').map(escapeHtmlText).join('<br>')}</p>`,
    )
    .join('');

// Runs on the sanitiser's own output, which is well-formed and escaped, so a
// tag never contains `>` and text never contains `<`.
const OUTPUT_TOKEN_RE = /<(\/?)([a-z]+)[^>]*>|[^<]+/g;
const EMPTY_PARAGRAPH_RE =
  /^<p>(?:\s|&nbsp;|&#160;|<br>|<\/?(?:strong|b|em|i|u)>)*<\/p>$/;
const BREAKS_ONLY_RE = /^(?:\s|<br>)*$/;

// One blank line between consecutive top-level blocks, whatever the model
// wrote between them (nothing, `<br>`, empty paragraphs); none at the ends.
const spaceTopLevelBlocks = (html: string): string => {
  const nodes: Array<{ html: string; block: boolean }> = [];
  let depth = 0;
  let start = 0;
  let block = false;
  for (const match of html.matchAll(OUTPUT_TOKEN_RE)) {
    const [token, closing, name] = match;
    if (depth === 0) {
      start = match.index;
      block = name != null && closing === '' && TOP_LEVEL_BLOCK_TAGS.has(name);
    }
    if (name != null && name !== 'br') {
      depth += closing === '' ? 1 : -1;
    }
    if (depth === 0) {
      const piece = html.slice(start, match.index + token.length);
      const last = nodes.at(-1);
      if (!block && last != null && !last.block) {
        last.html += piece;
      } else {
        nodes.push({ html: piece, block });
      }
    }
  }

  const parts: string[] = [];
  let pendingBlankLine = false;
  let previousIsBlock = false;
  for (const node of nodes) {
    if (
      node.block
        ? EMPTY_PARAGRAPH_RE.test(node.html)
        : BREAKS_ONLY_RE.test(node.html)
    ) {
      if ((node.block || node.html.includes('<br>')) && parts.length > 0) {
        pendingBlankLine = true;
      }
      continue;
    }
    if (
      parts.length > 0 &&
      (pendingBlankLine || (previousIsBlock && node.block))
    ) {
      parts.push(BLANK_LINE);
    }
    parts.push(node.html);
    previousIsBlock = node.block;
    pendingBlankLine = false;
  }
  return parts.join('');
};

// Only a known tag (or a comment/doctype) makes the input HTML: plain text
// such as `Jane <jane@agence.ch>` must keep its address and paragraphs. A
// misread the other way is harmless, the plain-text path escapes everything.
const KNOWN_TAG_RE = new RegExp(
  `<\\/?(?:${[
    ...ALLOWED_TAGS,
    ...RAW_TEXT_DROPPED_TAGS,
    ...NESTED_DROPPED_TAGS,
    'embed',
    'img',
    'picture',
    'source',
    'video',
    'audio',
    'link',
    'meta',
    'html',
    'body',
    'div',
    'span',
    'font',
    'h[1-6]',
    'table',
    'thead',
    'tbody',
    'tr',
    'td',
    'th',
    'hr',
  ].join('|')})(?=[\\s/>])|<!--|<!doctype`,
  'i',
);

const looksLikeHtml = (text: string): boolean => KNOWN_TAG_RE.test(text);

export const sanitizeAgentEmailHtml = (html: string): string => {
  if (!looksLikeHtml(html)) {
    return spaceTopLevelBlocks(plainTextToHtml(html));
  }

  const out: string[] = [];
  const open: Array<{ name: string; markup: string }> = [];
  let droppedTag: string | null = null;
  let droppedDepth = 0;
  // Where the current run of inline content outside any element starts in
  // `out`, so a paragraph break in it can wrap what came before in a `<p>`.
  let topLevelRunStart: number | null = null;

  const lastOpenIndex = (name: string): number =>
    open.findLastIndex(entry => entry.name === name);

  const pushOpen = (name: string, markup: string) => {
    out.push(markup);
    open.push({ name, markup });
  };

  const closeUntil = (name: string) => {
    const index = lastOpenIndex(name);
    if (index === -1) {
      return;
    }
    while (open.length > index) {
      out.push(`</${open.pop()?.name}>`);
    }
  };

  const pushInline = (markup: string) => {
    if (markup === '') {
      return;
    }
    if (open.length === 0 && topLevelRunStart == null) {
      topLevelRunStart = out.length;
    }
    out.push(markup);
  };

  // A blank line in the text: end the paragraph and open a new one with the
  // same inline formatting. Outside a paragraph (a list item, a bare
  // blockquote) it can only be a line break.
  const breakParagraph = () => {
    const blockIndex = open.findLastIndex(entry => BLOCK_TAGS.has(entry.name));
    if (blockIndex !== -1 && open[blockIndex]?.name !== 'p') {
      out.push('<br>');
      return;
    }
    const inline = open.slice(blockIndex + 1);
    if (blockIndex === -1) {
      while (open.length > 0) {
        out.push(`</${open.pop()?.name}>`);
      }
      if (topLevelRunStart != null) {
        out.splice(topLevelRunStart, 0, '<p>');
        out.push('</p>');
      }
      topLevelRunStart = null;
    } else {
      closeUntil('p');
    }
    pushOpen('p', '<p>');
    for (const entry of inline) {
      pushOpen(entry.name, entry.markup);
    }
  };

  // Newlines in HTML text would collapse to spaces in the composer: a blank
  // line starts a paragraph, a single newline is a line break, and newlines
  // that only format the source (around tags) stay plain whitespace.
  const pushText = (raw: string) => {
    const text = raw.replace(/\r\n?/g, '\n');
    if (!text.includes('\n')) {
      pushInline(escapeSourceText(text));
      return;
    }
    const pushEdge = (whitespace: string) => {
      if (!whitespace.includes('\n')) {
        pushInline(whitespace);
        return;
      }
      const inInlineContent =
        open.length > 0
          ? !BLOCK_CONTAINER_TAGS.has(open.at(-1)?.name ?? '')
          : topLevelRunStart != null;
      if (!inInlineContent) {
        return;
      }
      if (/\n[^\S\n]*\n/.test(whitespace)) {
        breakParagraph();
      } else {
        pushInline(' ');
      }
    };
    if (text.trim() === '') {
      pushEdge(text);
      return;
    }
    const leading = /^\s*/.exec(text)?.[0] ?? '';
    const trailing = /\s*$/.exec(text)?.[0] ?? '';
    pushEdge(leading);
    text
      .slice(leading.length, text.length - trailing.length)
      .split(/[^\S\n]*\n[^\S\n]*\n\s*/)
      .forEach((paragraph, index) => {
        if (index > 0) {
          breakParagraph();
        }
        pushInline(
          paragraph
            .split(/[^\S\n]*\n[^\S\n]*/)
            .map(escapeSourceText)
            .join('<br>'),
        );
      });
    pushEdge(trailing);
  };

  const openTag = (tag: ParsedTag) => {
    const { name } = tag;
    if (name === 'br') {
      pushInline('<br>');
      return;
    }
    if (name === 'a') {
      const href = sanitizeAgentHref(tag.attrs.get('href') ?? '');
      if (href == null) {
        return;
      }
      closeUntil('a');
      if (open.length === 0 && topLevelRunStart == null) {
        topLevelRunStart = out.length;
      }
      pushOpen('a', `<a href="${escapeAttr(href)}">`);
      return;
    }
    if (BLOCK_TAGS.has(name)) {
      closeUntil('p');
    }
    if (name === 'li') {
      const lastItem = lastOpenIndex('li');
      const lastList = Math.max(lastOpenIndex('ul'), lastOpenIndex('ol'));
      if (lastItem > lastList) {
        closeUntil('li');
      }
    }
    if (open.length === 0) {
      topLevelRunStart = BLOCK_TAGS.has(name)
        ? null
        : topLevelRunStart ?? out.length;
    }
    pushOpen(name, `<${name}>`);
  };

  let i = 0;
  while (i < html.length) {
    const lt = html.indexOf('<', i);
    const textEnd = lt === -1 ? html.length : lt;
    if (textEnd > i) {
      if (droppedTag == null) {
        pushText(html.slice(i, textEnd));
      }
      i = textEnd;
      continue;
    }

    const next = html[i + 1];
    if (html.startsWith('<!--', i)) {
      if (html.startsWith('<!-->', i)) {
        i += 5;
      } else if (html.startsWith('<!--->', i)) {
        i += 6;
      } else {
        const commentEnd = html.indexOf('-->', i + 4);
        i = commentEnd === -1 ? html.length : commentEnd + 3;
      }
      continue;
    }
    if (
      next === '!' ||
      next === '?' ||
      (next === '/' && !isAsciiAlpha(html[i + 2]))
    ) {
      const bogusEnd = html.indexOf('>', i);
      i = bogusEnd === -1 ? html.length : bogusEnd + 1;
      continue;
    }
    if (!isAsciiAlpha(next) && next !== '/') {
      if (droppedTag == null) {
        pushInline('&lt;');
      }
      i += 1;
      continue;
    }

    const tag = readTag(html, i);
    if (tag == null) {
      break;
    }
    const tagStart = i;
    i = tag.end;

    // `<jane@agence.ch>` is an address in angle brackets, not markup.
    if (droppedTag == null && tag.name.includes('@')) {
      pushInline(escapeHtmlText(html.slice(tagStart, tag.end)));
      continue;
    }

    if (droppedTag != null) {
      if (tag.name === droppedTag) {
        if (tag.closing) {
          droppedDepth -= 1;
        } else if (!tag.selfClosing) {
          droppedDepth += 1;
        }
        if (droppedDepth === 0) {
          droppedTag = null;
        }
      }
      continue;
    }

    if (tag.closing) {
      if (ALLOWED_TAGS.has(tag.name) && tag.name !== 'br') {
        closeUntil(tag.name);
      }
      continue;
    }
    if (RAW_TEXT_DROPPED_TAGS.has(tag.name)) {
      i = skipRawText(html, i, tag.name);
      continue;
    }
    if (NESTED_DROPPED_TAGS.has(tag.name)) {
      if (!tag.selfClosing) {
        droppedTag = tag.name;
        droppedDepth = 1;
      }
      continue;
    }
    if (ALLOWED_TAGS.has(tag.name)) {
      openTag(tag);
    }
  }

  while (open.length > 0) {
    out.push(`</${open.pop()?.name}>`);
  }
  return spaceTopLevelBlocks(out.join(''));
};
